// 音の窓口。素材は tools/audio（ElevenLabs で生成 → 加工）で作り、public/audio/manifest.json に一覧がある。
// 画面からはキー名で鳴らすだけ。初回操作前（ブラウザの自動再生制限）・未読込は無音で何もしない。
// BGM：場面ごとの曲は SCENE_BGM（main.ts の onRoute から）。合戦・戦果・ストーリーは画面が自分で選ぶ。
//      曲の切り替えはクロスフェード、ループはイントロ明け〜loopEnd。ジングル中は BGM を止め、終わってから次の曲を始める。
import { state } from './state';

export type SfxKey =
  // UI
  | 'tap' | 'tab' | 'open' | 'close' | 'confirm' | 'error' | 'toggle' | 'chat'
  // 報酬・成長
  | 'reward' | 'coin' | 'unlock' | 'enhance' | 'enhanceBig' | 'upgrade' | 'purchase' | 'stamp' | 'equip'
  // おまもりガチャ（キーは旧名のまま）：Shake・Stamp は使わない（Stamp は掃討の演出が使う）・光がはじける・確定の鈴・札をめくる・良/優/極の光・錬成
  | 'omikujiShake' | 'omikujiPop' | 'omikujiStamp' | 'omikujiSuzu' | 'omikujiFlip' | 'omikujiR' | 'omikujiSR' | 'omikujiSSR' | 'omamoriFuse'
  // 合戦
  | 'battleStart' | 'turnPlayer' | 'turnEnemy' | 'roll' | 'diceResult' | 'select' | 'move' | 'build' | 'capture' | 'lost'
  // 攻撃：使い手ごとの通常攻撃・術（見た目に合わせる）
  | 'atkAkari' | 'skillAkari' | 'atkKazamaru' | 'skillKazamaru' | 'atkSuzune' | 'skillSuzune' | 'atkYuzuha' | 'skillYuzuha' | 'atkClaw' | 'atkOrb'
  | 'hit' | 'hitHeavy' | 'skill' | 'ougiCharge' | 'ougi' | 'cutin' | 'armor' | 'counter' | 'tower' | 'cannon' | 'horoku' | 'kill' | 'killBig' | 'down'
  | 'coreHit' | 'coreDown' | 'heal' | 'potion' | 'surge' | 'status' | 'burn' | 'trap' | 'recall' | 'wave' | 'telegraph' | 'bossStrike';
export type BgmKey = 'title' | 'home' | 'village' | 'shop' | 'gacha' | 'map' | 'battle' | 'battle2' | 'boss' | 'boss2' | 'result'
  // ストーリー：場面に合わせて切り替える（stories.ts の @bgm と storyBgmOf）
  | 'story_daily' | 'story_warm' | 'story_journey' | 'story_tense' | 'story_mystery' | 'story_sad' | 'story_resolve' | 'story_triumph';
export type JingleKey = 'victory' | 'defeat' | 'levelup';

/** 画面 → BGM。載っていない画面（battle・result）は画面側で選ぶ。 */
export const SCENE_BGM: Record<string, BgmKey> = {
  login: 'title',
  top: 'home',
  chara: 'home',
  skills: 'home',
  enhance: 'home',
  dex: 'home',
  settings: 'home',
  ranking: 'home',
  missions: 'home',
  news: 'home',
  presents: 'home',
  stories: 'home',
  village: 'village',
  shop: 'shop',
  purchases: 'shop',
  gacha: 'gacha',
  stages: 'map',
};

type LoopInfo = { url: string; loopStart?: number; loopEnd?: number; dur?: number; lufs?: number };
/** BGM の音量をこの大きさ（LUFS）へそろえる。これより大きい曲だけ下げる（上げると割れるため）。ADV→合戦などの切り替えで音量が跳ねないように */
const BGM_LUFS = -14;
const levelOf = (info: LoopInfo) => (info.lufs == null ? 1 : Math.min(1, Math.pow(10, (BGM_LUFS - info.lufs) / 20)));
type Manifest = { bgm: Record<string, LoopInfo>; jingle: Record<string, LoopInfo>; se: Record<string, LoopInfo> };

let ctx: AudioContext | null = null;
let sfxGain: GainNode | null = null;
let musicGain: GainNode | null = null;
let manifest: Manifest | null = null;
let manifestLoad: Promise<void> | null = null;
const buffers = new Map<string, Promise<AudioBuffer | null>>();
const ready = new Map<string, AudioBuffer>();
/** BGM：圧縮のままのデータ（全曲を裏で先に取っておく。1曲 1MB 前後）と、展開済み（1曲 30MB 前後なので最近の3曲だけ） */
const bgmBytes = new Map<string, Promise<ArrayBuffer | null>>();
const bgmDecoded = new Map<string, Promise<AudioBuffer | null>>();
const BGM_KEEP = 4;
/** 今の曲の次に流れそうな曲（鳴らし始めたら裏で展開しておき、切り替えで展開を待たない） */
const NEXT_BGM: Partial<Record<BgmKey, BgmKey[]>> = {
  title: ['story_mystery'],
  home: ['map', 'village'],
  map: ['battle', 'home'],
  battle: ['result'],
  battle2: ['result'],
  boss: ['result'],
  boss2: ['result'],
  result: ['map', 'home'],
  village: ['home'],
  shop: ['home'],
  gacha: ['home'],
};
const lastPlayed = new Map<SfxKey, number>();

/** いま鳴っている（鳴らしたい）BGM */
let wantBgm: BgmKey | null = null;
let playing: { key: BgmKey; src: AudioBufferSourceNode; gain: GainNode } | null = null;
let jingleUntil = 0;
let jingleTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * 起動してすぐ（操作の前）に用意を始める：曲の一覧 → 今の画面の曲を取って展開 → 効果音・ジングル → 残りの曲を圧縮のまま。
 * AudioContext は操作の前に作ると止まった状態（suspended）で始まるが、取得・展開はできる。最初の操作で resume するだけで鳴る。
 */
/** Claude（Claude Code・デスクトップアプリ）の中のブラウザで開いたときは音を出さない（確認作業で鳴りっぱなしにしない） */
const SILENT = / Claude\//.test(navigator.userAgent);

function initAudio() {
  if (ctx || SILENT) return;
  const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AC) return;
  ctx = new AC({ latencyHint: 'interactive' });
  sfxGain = ctx.createGain();
  musicGain = ctx.createGain();
  // 全体の音量（閉じる・戻るときに一瞬で絞る／戻すため。いきなり止めると波形が途中で切れて「プツッ」と鳴る）
  const master = ctx.createGain();
  master.connect(ctx.destination);
  sfxGain.connect(master);
  musicGain.connect(master);
  applyVolume();
  // 画面を隠したら止め、戻ったら再開（電池と、裏で鳴りっぱなしにしないため）。止める前に音量を 0 へ絞る。
  // 絞りきる前に止めると波形が途中で切れて「プンッ」と鳴るので、出力の遅れ（Bluetooth などは長い）が過ぎてから止める
  const FADE = 0.12;
  let suspendTimer = 0;
  const fadeOut = () => {
    if (!ctx || ctx.state !== 'running') return;
    const t = ctx.currentTime;
    master.gain.cancelScheduledValues(t);
    master.gain.setValueAtTime(master.gain.value, t);
    master.gain.linearRampToValueAtTime(0, t + FADE);
    clearTimeout(suspendTimer);
    const lag = (ctx.outputLatency || 0) + (ctx.baseLatency || 0);
    suspendTimer = window.setTimeout(() => ctx && document.visibilityState === 'hidden' && void ctx.suspend().catch(() => {}), (FADE + lag) * 1000 + 120);
  };
  const fadeIn = () => {
    if (!ctx) return;
    clearTimeout(suspendTimer);
    // 音の装置を始められない端末（Bluetooth の切り替え中など）でも止めない（音が鳴らないだけ）
    void ctx.resume().then(() => {
      if (!ctx) return;
      const t = ctx.currentTime;
      master.gain.cancelScheduledValues(t);
      master.gain.setValueAtTime(master.gain.value, t);
      master.gain.linearRampToValueAtTime(1, t + 0.25);
    }).catch(() => {});
  };
  document.addEventListener('visibilitychange', () => (document.visibilityState === 'hidden' ? fadeOut() : fadeIn()));
  // iOS はアプリを閉じるとき pagehide が先に来ることがある
  window.addEventListener('pagehide', fadeOut);
  // スマホはアプリの切り替え（上へのスワイプ）を始めた時点で blur が来る。visibilitychange を待つと、OS に先に音を切られて「プンッ」と鳴るので、ここで絞り始める
  // （PC は別の窓を触っただけで来るので、指で操作する端末だけ）
  if (matchMedia('(pointer: coarse)').matches) {
    window.addEventListener('blur', fadeOut);
    window.addEventListener('focus', () => document.visibilityState === 'visible' && fadeIn());
  }
  window.addEventListener('pageshow', () => document.visibilityState === 'visible' && fadeIn());
  void loadManifest().then(async () => {
    // 用意の前に頼まれていた曲を先に（効果音の読み込みと回線を取り合わない）
    const k = wantBgm;
    wantBgm = null;
    if (k) await bgm(k);
    // 効果音・ジングルは全部読む（小さい）。そのあと BGM を圧縮のまま裏で全部取っておく（曲の切り替えで通信を待たない）
    await Promise.all([...Object.values(manifest?.se ?? {}), ...Object.values(manifest?.jingle ?? {})].map((v) => load(v.url)));
    for (const v of Object.values(manifest?.bgm ?? {})) await fetchBgm(v.url);
  });
}

/** 最初のユーザー操作で呼ぶ（ブラウザの自動再生制限）。用意は initAudio で済んでいるので、止まっていれば再開するだけ */
export function unlockAudio() {
  initAudio();
  if (ctx && ctx.state !== 'running' && document.visibilityState === 'visible') void ctx.resume().catch(() => {});
}

export function applyVolume() {
  const m = state.settings.master ?? 1;
  if (sfxGain) sfxGain.gain.value = state.settings.sfx * m;
  if (musicGain) musicGain.gain.value = state.settings.bgm * 0.8 * m;
}

function loadManifest() {
  manifestLoad ??= fetch('/audio/manifest.json', { cache: 'no-cache' })
    .then((r) => (r.ok ? r.json() : null))
    .then((m: Manifest | null) => {
      manifest = m;
    })
    .catch(() => {});
  return manifestLoad;
}

function load(url: string): Promise<AudioBuffer | null> {
  let p = buffers.get(url);
  if (!p) {
    p = (async () => {
      if (!ctx) return null;
      try {
        const res = await fetch(url);
        if (!res.ok) return null;
        const buf = await ctx.decodeAudioData(await res.arrayBuffer());
        ready.set(url, buf);
        return buf;
      } catch {
        return null;
      }
    })();
    buffers.set(url, p);
  }
  return p;
}

function fetchBgm(url: string) {
  let p = bgmBytes.get(url);
  if (!p) {
    p = fetch(url)
      .then((r) => (r.ok ? r.arrayBuffer() : null))
      .catch(() => null);
    bgmBytes.set(url, p);
  }
  return p;
}

/** BGM を展開する（最近使った順に BGM_KEEP 曲だけ残す） */
function loadBgm(url: string): Promise<AudioBuffer | null> {
  let p = bgmDecoded.get(url);
  if (p) {
    bgmDecoded.delete(url); // 使った順に並べ直す
  } else {
    p = (async () => {
      const bytes = await fetchBgm(url);
      if (!bytes || !ctx) return null;
      try {
        return await ctx.decodeAudioData(bytes.slice(0)); // 展開すると渡したデータは使えなくなるので写しを渡す
      } catch {
        return null;
      }
    })();
  }
  bgmDecoded.set(url, p);
  while (bgmDecoded.size > BGM_KEEP) bgmDecoded.delete(bgmDecoded.keys().next().value!);
  return p;
}

/** 効果音。同じ音の連打は 40ms 以内なら間引く。rate で少し音程を変えると単調にならない */
export function sfx(key: SfxKey, volume = 1, rate = 1) {
  const note = (result: string) => (lastSfx = { key, result, at: performance.now() });
  if (!ctx || !sfxGain) return void note('音が未有効');
  if (state.settings.sfx <= 0) return void note('音量0');
  if (ctx.state !== 'running') return void note(`停止中(${ctx.state})`);
  const url = manifest?.se[key]?.url;
  const buf = url ? ready.get(url) : undefined;
  if (!buf) {
    if (url) void load(url);
    return void note('未読込');
  }
  const now = performance.now();
  if (now - (lastPlayed.get(key) ?? 0) < 40) return void note('連打で間引き');
  lastPlayed.set(key, now);
  note('鳴らした');
  const src = ctx.createBufferSource();
  src.buffer = buf;
  src.playbackRate.value = rate;
  const g = ctx.createGain();
  g.gain.value = volume;
  src.connect(g).connect(sfxGain);
  src.onended = () => {
    src.disconnect();
    g.disconnect();
  };
  src.start();
}

/** 計測用（?audiodebug=1 の表示）：最後に頼まれた効果音とその結果 */
let lastSfx: { key: string; result: string; at: number } | null = null;
export function audioStats() {
  const c = ctx as (AudioContext & { outputLatency?: number }) | null;
  const count = (m: Record<string, LoopInfo> | undefined) => Object.values(m ?? {}).filter((v) => ready.has(v.url)).length;
  return {
    state: c?.state ?? 'なし',
    sampleRate: c?.sampleRate ?? 0,
    baseLatency: Math.round((c?.baseLatency ?? 0) * 1000),
    outputLatency: Math.round((c?.outputLatency ?? 0) * 1000),
    se: `${count(manifest?.se)}/${Object.keys(manifest?.se ?? {}).length}`,
    jingle: `${count(manifest?.jingle)}/${Object.keys(manifest?.jingle ?? {}).length}`,
    bgmFetched: bgmBytes.size,
    bgmDecoded: [...bgmDecoded.keys()].map((u) => u.split('/').pop()!.split('.')[0]),
    playing: playing?.key ?? null,
    want: wantBgm,
    lastSfx,
  };
}

/** 打撃・移動など何度も鳴る音は、音程を少し揺らす */
export const vary = (amount = 0.06) => 1 + (Math.random() * 2 - 1) * amount;

function fadeOutAndStop(p: NonNullable<typeof playing>, sec: number) {
  if (!ctx) return;
  const t = ctx.currentTime;
  p.gain.gain.cancelScheduledValues(t);
  p.gain.gain.setValueAtTime(p.gain.gain.value, t);
  p.gain.gain.linearRampToValueAtTime(0, t + sec);
  try {
    p.src.stop(t + sec + 0.05);
  } catch {
    /* 止まっている */
  }
}

/** BGM を切り替える（同じ曲なら何もしない）。null で止める。
 *  次の曲の用意ができるまで今の曲は鳴らし続け、できたらクロスフェード（無音の隙間を作らない）。ジングル中なら終わってから始める */
export async function bgm(key: BgmKey | null, opts: { fade?: number } = {}) {
  const fade = opts.fade ?? 0.4;
  wantBgm = key;
  if (!ctx || !musicGain || !manifest) return;
  if (playing?.key === key) return;
  if (!key) {
    if (playing) fadeOutAndStop(playing, fade);
    playing = null;
    return;
  }
  const info = manifest.bgm[key];
  if (!info) return;
  const buf = await loadBgm(info.url);
  // 用意している間に別の曲を頼まれた／同じ曲がもう鳴っている
  if (!buf || wantBgm !== key || playing?.key === key) return;
  const wait = Math.max(0, jingleUntil - performance.now());
  if (wait > 0) {
    if (jingleTimer) clearTimeout(jingleTimer);
    jingleTimer = setTimeout(() => {
      jingleTimer = null;
      if (wantBgm === key && playing?.key !== key) void bgm(key);
    }, wait);
    return;
  }
  if (playing) fadeOutAndStop(playing, fade);
  const src = ctx.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  src.loopStart = info.loopStart ?? 0;
  src.loopEnd = info.loopEnd ?? buf.duration;
  const g = ctx.createGain();
  const t = ctx.currentTime;
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(levelOf(info), t + fade);
  src.connect(g).connect(musicGain);
  src.start();
  playing = { key, src, gain: g };
  for (const next of NEXT_BGM[key] ?? []) prepareBgm(next);
}

/** 物語の曲の種類（台本の @bgm）→ BGM。boss は合戦のボス戦の曲、それ以外は物語用の曲 */
export const storyBgmKey = (m: string): BgmKey => (m === 'boss' ? 'boss' : (`story_${m}` as BgmKey));

/** 次に流れそうな曲を先に展開しておく（切り替えの瞬間に無音を作らない）。展開済みは最近の BGM_KEEP 曲まで */
export function prepareBgm(key: BgmKey) {
  void loadManifest().then(() => {
    const info = manifest?.bgm[key];
    if (info && ctx && !bgmDecoded.has(info.url)) void loadBgm(info.url);
  });
}

/** ジングル（勝利・敗北・レベルアップ）。BGM を素早く下げて止め、ジングルが終わるまで次の BGM を待たせる */
export async function jingle(key: JingleKey, opts: { keepBgm?: boolean } = {}) {
  if (!ctx || !musicGain || !manifest) return;
  const info = manifest.jingle[key];
  if (!info) return;
  const dur = (info.dur ?? 6) * 1000;
  // 前のジングルが鳴っていたら終わってから
  const wait = jingleUntil - performance.now();
  if (wait > 50) {
    if (!opts.keepBgm) jingleUntil += dur; // 次の BGM がこの分も待つように
    return void setTimeout(() => {
      jingleUntil = performance.now();
      void jingle(key, opts);
    }, wait);
  }
  if (!opts.keepBgm) {
    // 読み込み中に次の画面が BGM を頼んでも、ジングルが終わるまで待たせる
    jingleUntil = performance.now() + dur + 300;
    if (playing) {
      fadeOutAndStop(playing, 0.3);
      playing = null;
    }
  }
  const buf = await load(info.url);
  if (!buf) {
    jingleUntil = 0;
    return;
  }
  const src = ctx.createBufferSource();
  src.buffer = buf;
  src.connect(musicGain);
  src.start();
  if (opts.keepBgm) return;
  jingleUntil = performance.now() + buf.duration * 1000;
  // 終わったら、止めた曲（または頼まれている曲）を始める
  src.onended = () => {
    if (wantBgm && !playing && performance.now() >= jingleUntil - 50) void bgm(wantBgm);
  };
}

initAudio();
